import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const vence: ChauffeurPage = {
  slug: "vence",
  title: "Chauffeur privé à Vence – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Vence : chapelle Matisse, cité historique, place du Peyra, porte de l’arrière-pays et transfert aéroport de Nice en ~35 min.",
  keywords: [
    "chauffeur privé vence",
    "vtc vence",
    "chauffeur chapelle matisse",
    "transfert vence aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Vence",
    subtitle:
      "La cité historique et sa chapelle Matisse, à la porte de l’arrière-pays niçois : un chauffeur pour la ville et les routes du haut-pays.",
    keywords: [
      "VTC Vence",
      "chauffeur chapelle Matisse",
      "transfert aéroport Nice",
      "chauffeur arrière-pays",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Vence",
    badges: ["Chapelle Matisse", "Cité historique", "~35 min de l’aéroport"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Vence",
    items: [
      {
        title: "Chapelle du Rosaire de Matisse",
        description:
          "Le joyau de Vence n’ouvre que sur certains créneaux : le chauffeur cale la visite sur les horaires d’ouverture et vous dépose devant, sans perdre de temps.",
      },
      {
        title: "Cité historique piétonne",
        description:
          "Place du Peyra et sa fontaine, portes anciennes et ruelles fermées aux voitures : dépose au plus près des portails, reprise à la sortie de la vieille ville.",
      },
      {
        title: "Porte de l’arrière-pays",
        description:
          "Vence ouvre sur Tourrettes-sur-Loup, les Gorges du Loup et Coursegoules : un chauffeur qui connaît les routes de montagne et leurs lacets.",
      },
      {
        title: "À ~35 minutes de l’aéroport",
        description:
          "Transfert direct depuis et vers l’aéroport de Nice Côte d’Azur, avec suivi de vol et accueil à l’arrivée.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Vence",
    services: [
      {
        title: "Visite de la cité historique",
        text: "Dépose aux portes de la vieille ville, attente le temps de flâner autour de la place du Peyra et de la cathédrale, reprise sur appel.",
      },
      {
        title: "Chapelle Matisse",
        text: "Trajet calé sur les horaires d’ouverture de la chapelle du Rosaire, avec dépose devant et temps d’attente inclus.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Vence ⇄ aéroport de Nice en ~35 min, via La Gaude et l’A8, avec suivi de vol.",
      },
      {
        title: "Journée dans l’arrière-pays",
        text: "Mise à disposition pour enchaîner Tourrettes-sur-Loup, les Gorges du Loup et les villages perchés, à votre rythme.",
      },
      {
        title: "Soirées & tables de Vence",
        text: "Restaurants de la vieille ville et alentours : aller-retour de soirée sans reprendre le volant.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Place du Peyra",
        description: "Cœur de la cité historique, sa fontaine et son marché.",
      },
      {
        name: "Porte du Peyra",
        description:
          "Entrée de la vieille ville, au plus près des ruelles piétonnes.",
      },
      {
        name: "Chapelle du Rosaire",
        description: "La chapelle décorée par Matisse, route de Saint-Jeannet.",
      },
      {
        name: "Cathédrale de la Nativité",
        description:
          "Édifice roman au centre de la cité, ses mosaïques et son retable.",
      },
      {
        name: "Château de Villeneuve",
        description: "Fondation Émile Hugues, espace d’expositions d’art.",
      },
      {
        name: "Route des Gorges du Loup",
        description:
          "Vers Tourrettes-sur-Loup et le haut-pays, en direction du Loup.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur pour Vence et le haut-pays",
    subheading:
      "Une cité vivante au pied des baous, entre chapelle Matisse et routes de l’arrière-pays.",
    paragraphs: [
      "Vence n’est pas qu’une étape touristique : c’est une cité habitée, ceinte de remparts, où les voitures s’arrêtent aux portes. Le chauffeur vous laisse au seuil de la vieille ville, le temps d’une flânerie place du Peyra ou d’une visite à la cathédrale, puis vous reprend sans que le stationnement n’entre en jeu.",
      "KS Rent Services propose des forfaits fixes annoncés à l’avance — dont Vence ⇄ aéroport de Nice — et la mise à disposition à l’heure ou à la journée, avec devis immédiat. Nos chauffeurs bilingues français/anglais, basés sur la Côte d’Azur, affichent une note de 5,0/5 sur 38 avis Google.",
      "Vence sert aussi de tremplin vers l’arrière-pays : Tourrettes-sur-Loup, les Gorges du Loup, Coursegoules et les cols. Sur ces routes sinueuses, un chauffeur dédié conduit pendant que vous profitez du paysage, aller comme retour.",
    ],
    bullets: [
      "Dépose aux portes de la cité, reprise sur appel",
      "Forfaits fixes & mise à disposition, devis immédiat",
      "Chauffeurs bilingues FR/EN, 5,0/5 sur 38 avis Google",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Vence",
    steps: [
      "Indiquez votre programme : chapelle Matisse, vieille ville, arrière-pays ou transfert aéroport.",
      "Nous confirmons le véhicule et le forfait ou la mise à disposition, horaires de la chapelle compris.",
      "Le chauffeur vous dépose aux portes de la cité ou à l’adresse voulue.",
      "Visite à votre rythme, reprise sur appel pour la suite du programme.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Vence"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Vence ?",
      a: "Environ 35 minutes via La Gaude et l’A8. Le chauffeur suit votre vol et adapte l’heure de départ selon le trafic sur la descente vers la côte.",
    },
    {
      q: "Pouvez-vous organiser la visite de la chapelle Matisse ?",
      a: "Oui. La chapelle du Rosaire n’ouvre que sur des créneaux précis ; nous calons le trajet sur ses horaires, avec dépose devant l’entrée et temps d’attente inclus.",
    },
    {
      q: "Desservez-vous l’arrière-pays et les Gorges du Loup ?",
      a: "Oui, en mise à disposition à la journée pour relier Tourrettes-sur-Loup, les Gorges du Loup et Coursegoules, avec un chauffeur habitué aux routes de montagne.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez tôt au printemps et en été, saisons chargées à Vence ; les demandes de dernière minute sont traitées selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, ils sont bilingues français/anglais et à l’aise avec une clientèle internationale de passage à Vence.",
    },
    {
      q: "Quels véhicules proposez-vous pour Vence ?",
      a: "Berline Mercedes, van ou SUV selon le groupe, avec des véhicules à l’aise aussi bien en ville que sur les routes de l’arrière-pays.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    { label: "Réserver un chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Chauffeur privé sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Voitures à louer à Vence",
      href: "/location/location-voiture-vence",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Vence",
    text: "Chapelle Matisse, cité historique ou escapade dans l’arrière-pays : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Vence 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Vence : chapelle Matisse, cité historique, arrière-pays et transfert aéroport de Nice en ~35 min. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/vence",
  },
};

export default vence;
